import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { MailCheck, AlertCircle, CheckCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { API_BASE_URL } from "../config";

export const VerifyEmailPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsEmailVerified, setAuthError } = useAuth();

  const [statusText, setStatusText] = useState("Verifying...");
  const [isSuccess, setIsSuccess] = useState(false);
  const [loading, setLoading] = useState(true);

  // Resend helpers
  const qsEmail = useMemo(() => {
    const p = new URLSearchParams(location.search);
    return p.get("email") || "";
  }, [location.search]);
  const [resendEmail, setResendEmail] = useState(qsEmail);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMsg, setResendMsg] = useState(null);

  const abortRef = useRef(null);
  const redirectAfterMs = 2500; // set to 0 to disable auto-redirect

  const safeParse = async (resp) => {
    try {
      return await resp.json();
    } catch {
      const text = await resp.text().catch(() => "");
      return text ? { message: text } : {};
    }
  };

  useEffect(() => {
    if (!token) {
      setStatusText("Invalid verification link — missing token.");
      setIsSuccess(false);
      setLoading(false);
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const verifyEmail = async () => {
      setLoading(true);
      setAuthError(null);
      try {
        const resp = await fetch(`${API_BASE_URL}/api/verify-email/${token}/`, {
          headers: { Accept: "application/json" },
          signal: controller.signal
        });

        const data = await safeParse(resp);

        if (resp.ok) {
          setIsSuccess(true);
          setIsEmailVerified(true);
          setStatusText(data.message || "Email address verified!");
          if (redirectAfterMs > 0) {
            setTimeout(() => navigate("/"), redirectAfterMs);
          }
        } else {
          // Status-specific copy
          let msg =
            data.message ||
            (resp.status === 410
              ? "Verification link expired. Request a new one."
              : "Verification failed. The link may be invalid or already used.");
          setIsSuccess(false);
          setStatusText(msg);
          setAuthError(msg);
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        const msg = "An error occurred during verification. Try again later.";
        setIsSuccess(false);
        setStatusText(msg);
        setAuthError(msg);
      } finally {
        setLoading(false);
      }
    };

    verifyEmail();
    return () => controller.abort();
  }, [token, navigate, setIsEmailVerified, setAuthError]);

  const handleResend = async (e) => {
    e.preventDefault();
    setResendMsg(null);
    if (!resendEmail) {
      setResendMsg("Provide an email address to send a new link.");
      return;
    }
    setResendLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/resend-verification/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ email: resendEmail })
      });
      const data = await safeParse(resp);
      if (resp.ok) {
        setResendMsg(data.message || "A new link has been sent. Check your inbox.");
      } else {
        setResendMsg(
          data.message || "Unable to send a new link. Make sure the address is correct."
        );
      }
    } catch {
      setResendMsg("Network error. Try again.");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-10 rounded-xl shadow-lg text-center">
        {loading ? (
          <div className="flex flex-col items-center">
            <MailCheck className="text-blue-500 animate-pulse" size={48} />
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Email verification in progress...
            </h2>
            <p className="mt-2 text-sm text-gray-600">Please wait.</p>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            {isSuccess ? (
              <CheckCircle className="text-green-500" size={48} />
            ) : (
              <AlertCircle className="text-red-500" size={48} />
            )}
            <h2
              className={`mt-6 text-center text-3xl font-extrabold ${
                isSuccess ? "text-green-700" : "text-red-700"
              }`}
              aria-live="polite"
            >
              {statusText}
            </h2>

            {isSuccess ? (
              <p className="mt-4 text-sm text-gray-600">
                {redirectAfterMs > 0 ? "You will be redirected shortly..." : null}
              </p>
            ) : (
              <>
                <p className="mt-4">
                  <a href="/" className="text-blue-600 hover:text-blue-800 font-medium">
                    Go to login page
                  </a>
                </p>

                {/* Resend flow (shown if failed) */}
                <div className="mt-6 w-full text-left">
                  <h3 className="text-sm font-semibold text-gray-800 mb-2">
                    Send a new verification link
                  </h3>
                  <form onSubmit={handleResend} className="space-y-2">
                    <input
                      type="email"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Your email address"
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                    />
                    <button
                      type="submit"
                      disabled={resendLoading}
                      className="w-full py-2 px-4 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-60"
                    >
                      {resendLoading ? "Sending..." : "Send new link"}
                    </button>
                  </form>
                  {resendMsg && (
                    <p className="mt-2 text-sm" aria-live="polite">
                      {resendMsg}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
